// eslint-disable-next-line import/no-cycle
import {
  ComponentData, ComponentMetadata, ErrorConfigForm, Period,
} from '@/types/automated-scenarios';
// eslint-disable-next-line import/no-cycle
import { decreaseNumberOfBranchesOfAnOperator } from '@/composables/AutomatedScenarios';
import { computed } from 'vue';
import {
  integer, maxLength, minLength, required, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
// eslint-disable-next-line import/no-cycle
import { includesValuesValidator } from '@/helpers/CustomValidator';
// eslint-disable-next-line import/no-cycle
import { TypeCampaignEnum } from '@/types';

export interface FiltreFilledoutFormData {
  custom_title: string;
  custom_description: string;
  outputs_number: number;
  initial_number_outputs?: null|number; // for updating outputs
  grant_additional_delay: string;
  extra_time: Period;
  type: string;
  linked_box: string;
}

const FiltreFilledoutFormMetadata: ComponentMetadata<FiltreFilledoutFormData> = {
  Meta: {
    kind: 'filtre',
    id: 'filledoutform',
    label: 'automatedScenarios.filters.filledOutForm',
    icon: 'fab fa-wpforms',
    hasConfiguration: true,
    incompatibilities: {
      automation: {
        next_operators: [
          'boxabandonpanier', 'boxperiodiclaunching', 'boxvisitpage',
        ],
      },
      bulk: {
        next_operators: [
          'boxchoosebulklist', 'boxaudiencesetup',
        ],
      },
    },
    dependencies: {
      automation: {
        operators: {
          any_parents: ['boxsendemail', 'boxdisplayfreemodel', 'boxdisplayfreepopup'],
        },
      },
      bulk: {
        operators: {
          any_parents: ['boxsendemail', 'boxdisplayfreemodel', 'boxdisplayfreepopup'],
        },
      },
    },
    component: 'FiltreFilledoutForm',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
  },
  Create(data: FiltreFilledoutFormData = {
    custom_title: '',
    custom_description: '',
    outputs_number: 1,
    grant_additional_delay: '1',
    extra_time: {
      value: 1,
      unit: 'DAY',
    },
    type: 'filled',
    linked_box: '[]',
  }): ComponentData<FiltreFilledoutFormData> {
    return {
      id: 'filledoutform',
      form_name: 'filledoutform',
      logo_box: 'fab fa-wpforms',
      ...data,
    };
  },
  async Validate(data: FiltreFilledoutFormData): Promise<ErrorConfigForm> {
    const rules = computed(() => ({
      custom_title: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(45),
      },
      outputs_number: {
        required,
        includesValuesValidator: includesValuesValidator([1, 2]),
      },
      grant_additional_delay: {
        required,
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      extra_time: {
        value: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          integer,
        },
        unit: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          includesValuesValidator: includesValuesValidator(['SECOND', 'MINUTE', 'HOUR', 'DAY']),
        },
      },
      type: {
        required,
        includesValuesValidator: includesValuesValidator(['filled', 'nofilled']),
      },
      linked_box: {
        required,
        minLength: minLength(4),
      },
    }));

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },
  BeforeInsertData(data: FiltreFilledoutFormData): FiltreFilledoutFormData {
    const dataToReturn: FiltreFilledoutFormData = data;
    const { initial_number_outputs } = dataToReturn;

    if (typeof dataToReturn.initial_number_outputs !== 'undefined') {
      delete dataToReturn.initial_number_outputs;
    }

    if (initial_number_outputs !== null && initial_number_outputs !== undefined
      && data.outputs_number < initial_number_outputs) {
      /* En cas de dimunition du nombre de branches, on supprime la branche "non" */
      decreaseNumberOfBranchesOfAnOperator(['output_2']);
    }

    return dataToReturn;
  },
  Outputs(data: FiltreFilledoutFormData): number {
    return data.outputs_number;
  },
  Label(data: FiltreFilledoutFormData): string {
    return data.custom_title;
  },
};

export default FiltreFilledoutFormMetadata;
